/**
 * Every game the league has played.
 *
 * The leaderboards answer "what is the most, ever". This answers the question that always comes
 * straight afterwards — "hang on, what actually happened that week?" — and it is the only view
 * here worth showing for a season whose managers are still unknown, because the SCORE is recorded
 * even when the man is not.
 *
 * Box scores hang off a row rather than living on their own screen, because nobody goes looking
 * for "the 2007 week 4 box score" — they arrive at it from an argument about a result. Expanding a
 * game shows both lineups with the points each player scored, bench included, so the two questions
 * that follow a bad week ("who did this to me" and "what did I leave on the bench") are answered
 * in the same place.
 *
 * They are collected a page at a time from Yahoo, so a game may not have one yet. Where that is
 * true the row says so rather than showing an empty table that reads like a zero.
 */

import { useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import {
  GAME_SEASONS, allGameRows, gameWeeks, type GameRow, type Scope,
} from '../domain/records.js';
import { GmBadge } from './GmBadge.js';
import { managerShort } from '../domain/identity.js';
import {
  boxCoverage, boxVersion, clearLoadedBoxScores, loadBoxScores, onBoxesChanged,
} from '../domain/boxScores.js';
import { GameDetail } from './GameDetail.js';
import { GateTag } from './Gate.js';
import { atLeast, type Role } from '../store/types.js';

export function Games(
  { scope, scopeLabel, role, focus }: {
    scope: Scope; scopeLabel: string; role: Role;
    /**
     * A matchup to jump straight to — sent by an Ask the Record result.
     *
     * It sets the season and week filters as well as opening the row, because a game found in a
     * list of 1,770 that the filters then hide is not found at all.
     */
    focus?: { season: number; week: number; team: string } | null;
  },
) {
  const boxes = useSyncExternalStore(onBoxesChanged, boxVersion, boxVersion);
  const [season, setSeason] = useState<number | 'all'>(GAME_SEASONS[0] ?? 'all');
  const [week, setWeek] = useState<number | 'all'>('all');
  const [kind, setKind] = useState<'all' | 'regular' | 'postseason'>('all');
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<'week' | 'total' | 'margin' | 'blowout'>('week');
  const [open, setOpen] = useState<string | null>(null);
  const [limit, setLimit] = useState(60);

  /*
   * Follow a link in, in two steps.
   *
   * The filters first, because a game found among 1,770 that the filters then hide is not found.
   * Then the row id, which cannot be built from the link directly: a row is keyed by its HOME
   * team and the link names whichever side the question was about, which is the away team half
   * the time. So it is matched against either side once the filtered rows exist.
   */
  useEffect(() => {
    if (!focus) return;
    setSeason(focus.season);
    setWeek(focus.week);
    setKind('all');
    setQ('');
    setLimit(200);
  }, [focus?.season, focus?.week, focus?.team]);

  const weeks = useMemo(gameWeeks, []);
  const rows = useMemo(() => {
    const list = allGameRows({
      ...scope,
      ...(season === 'all' ? {} : { season: (y: number) => y === season && (scope.season?.(y) ?? true) }),
      week,
      kind,
      q,
    });
    if (sort === 'total') return [...list].sort((a, b) => b.total - a.total);
    if (sort === 'margin') return [...list].sort((a, b) => a.margin - b.margin);
    if (sort === 'blowout') return [...list].sort((a, b) => b.margin - a.margin);
    return list;
  }, [scope, season, week, kind, q, sort]);

  useEffect(() => {
    if (!focus) return;
    const norm = (t: string) => t.replace(/\s+/g, ' ').trim().toLowerCase();
    const want = norm(focus.team);
    const hit = rows.find((g) => g.season === focus.season && g.week === focus.week
      && (norm(g.home.team) === want || norm(g.away.team) === want));
    if (hit) setOpen(`${hit.season}-${hit.week}-${hit.home.team}`);
  }, [rows, focus?.season, focus?.week, focus?.team]);

  const shown = rows.slice(0, limit);

  return (
    <section className="card games">
      <div className="striptop">
        <h3 className="striphead">Every game</h3>
        <span className="mut">
          {rows.length.toLocaleString()} game{rows.length === 1 ? '' : 's'}
          {scopeLabel ? ` · ${scopeLabel}` : ''}
        </span>
      </div>

      <div className="toolbar gamebar">
        <input className="search" placeholder="Manager or team name" value={q}
          onChange={(e) => { setQ(e.target.value); setLimit(60); }} />
        <label className="inline">
          <span className="mut">Season</span>
          <select value={String(season)}
            onChange={(e) => { setSeason(e.target.value === 'all' ? 'all' : Number(e.target.value)); setLimit(60); }}>
            <option value="all">All</option>
            {GAME_SEASONS.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
        </label>
        <label className="inline">
          <span className="mut">Week</span>
          <select value={String(week)}
            onChange={(e) => setWeek(e.target.value === 'all' ? 'all' : Number(e.target.value))}>
            <option value="all">All</option>
            {weeks.map((w) => <option key={w} value={w}>{w}</option>)}
          </select>
        </label>
        <label className="inline">
          <span className="mut">Games</span>
          <select value={kind} onChange={(e) => setKind(e.target.value as typeof kind)}>
            <option value="all">All</option>
            <option value="regular">Regular season</option>
            <option value="postseason">Postseason</option>
          </select>
        </label>
        <label className="inline">
          <span className="mut">Sort</span>
          <select value={sort} onChange={(e) => setSort(e.target.value as typeof sort)}>
            <option value="week">Newest first</option>
            <option value="total">Highest scoring</option>
            <option value="blowout">Biggest margin</option>
            <option value="margin">Closest</option>
          </select>
        </label>
      </div>

      <BoxScoreSource role={role} version={boxes} />

      {rows.length === 0 && <p className="note">No games match that.</p>}

      <ul className="gamelist">
        {shown.map((g) => {
          const id = `${g.season}-${g.week}-${g.home.team}`;
          return (
            <Game key={id} g={g} boxes={boxes}
              open={open === id} onToggle={() => setOpen(open === id ? null : id)} />
          );
        })}
      </ul>

      {rows.length > shown.length && (
        <div className="actions">
          <button type="button" className="btn" onClick={() => setLimit(limit + 200)}>
            Show more ({(rows.length - shown.length).toLocaleString()} left)
          </button>
        </div>
      )}
    </section>
  );
}

function Game(
  { g, open, onToggle, boxes }:
  { g: GameRow; open: boolean; onToggle: () => void; boxes: number },
) {
  const side = (s: GameRow['home'], win: boolean) => (
    <span className={`gside ${win ? 'won' : ''}`}>
      {s.gm ? <GmBadge gm={s.gm} /> : <span className="gmdot unknown" aria-hidden="true">?</span>}
      <b>{s.gm ? managerShort(s.gm) : s.team}</b>
      {s.gm && <span className="mut tm">{s.team}</span>}
      <span className="pts">{s.points.toFixed(2)}</span>
    </span>
  );

  return (
    <li className={`gamerow ${open ? 'on' : ''}`}>
      <button type="button" className="gamehead" onClick={onToggle} aria-expanded={open}>
        <span className="when">
          {g.season} <span className="mut">wk {g.week}</span>
          {g.kind !== 'regular' && <span className="chip tiny">{g.kind}</span>}
        </span>
        {side(g.home, g.winner === 'home')}
        <span className="vs" aria-hidden="true">v</span>
        {side(g.away, g.winner === 'away')}
        <span className="mut margin">
          {g.winner === 'tie' ? 'tied' : `by ${g.margin.toFixed(2)}`}
        </span>
        {/*
          A row that opens has to LOOK like a row that opens. Every game here has a full box score
          behind it — who was started, who was left on the bench, and what each of them scored —
          and there was nothing on the row to say so, so the whole thing went unread.
        */}
        <span className="expand">
          <span className="lbl">{open ? 'Hide' : 'Box score'}</span>
          <i className="caret" aria-hidden="true" />
        </span>
      </button>

      {open && <GameDetail g={g} boxes={boxes} />}
    </li>
  );
}

/**
 * Where the box scores came from, and how to add more.
 *
 * The scrape produces one file. Loading it here puts it in front of the league immediately rather
 * than waiting for a rebuild — but it lives in this browser only, and the panel says so, because
 * a file on one laptop is not the same thing as history the league has.
 */
function BoxScoreSource({ role, version }: { role: Role; version: number }) {
  const cov = useMemo(boxCoverage, [version]);
  const canEdit = atLeast(role, 'commissioner');
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const ref = useRef<HTMLInputElement>(null);

  async function take(file: File) {
    setErr(null);
    try {
      const out = loadBoxScores(JSON.parse(await file.text()));
      setMsg(`Loaded ${out.games.toLocaleString()} games — ${out.seasons[0]}–${out.seasons.at(-1)}.`);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'That file could not be read.');
    }
  }

  return (
    <div className={`boxsource ${canEdit ? 'gated commish' : ''}`}>
      {canEdit && <GateTag level="commish" what="Commissioners — box scores" />}
      <p className="note">
        {cov.games === 0
          ? 'No box scores yet. Team totals are complete for all 1,770 games; the per-player lines behind them are gathered from Yahoo one matchup page at a time.'
          : `Box scores for ${cov.games.toLocaleString()} games, ${cov.seasons[0]}–${cov.seasons.at(-1)}.`}
        {cov.loadedGames > 0 && (
          <> <b>{cov.loadedGames.toLocaleString()} of them are loaded in this browser only</b> and
            will be gone on reload until they ship with a build.</>
        )}
      </p>
      {canEdit && (
        <div className="actions right">
          <input ref={ref} type="file" accept="application/json,.json" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void take(f); e.target.value = ''; }} />
          {cov.loadedGames > 0 && (
            <button type="button" className="btn" onClick={() => { clearLoadedBoxScores(); setMsg(null); }}>
              Unload
            </button>
          )}
          <button type="button" className="btn" onClick={() => ref.current?.click()}>
            Load a box-score file
          </button>
        </div>
      )}
      {msg && <p className="chip good block">{msg}</p>}
      {err && <p className="chip critical block">{err}</p>}
    </div>
  );
}
